import { type Client, createClient } from "@khazzin/api-client";
import { useFlag as useFlagFor } from "@khazzin/api-client/query";
import { useAccessToken, useInterface } from "@khazzin/auth";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { activeStore } from "./app/shell/activeStore";
import { impersonationToken } from "./app/shell/impersonation";

const ApiContext = createContext<Client | null>(null);

/** The API client. Throws outside {@link ApiClientProvider}, which is a wiring bug. */
export function useApi(): Client {
	const api = useContext(ApiContext);
	if (api === null) {
		throw new Error("useApi must be used inside <ApiClientProvider>");
	}
	return api;
}

/** Whether a feature flag is on for this app's interface (EP10-S23). Absent is off. */
export function useFlag(key: string): boolean {
	return useFlagFor(useApi(), useInterface(), key);
}

/**
 * A client built from the live token that names this app on every request —
 * `/me/context` included — through the interface `AuthProvider` was given.
 */
export function ApiClientProvider({ children }: { children: ReactNode }) {
	const signedIn = useAccessToken();
	const caller = useInterface();
	// EP01-S16: a tab opened by Login as Store carries the session's token
	// instead of a sign-in.
	const impersonation = impersonationToken();
	const accessToken = useMemo(
		() => (impersonation === null ? signedIn : async () => impersonation),
		[impersonation, signedIn],
	);
	const api = useMemo(
		() =>
			createClient({
				accessToken,
				interface: caller,
				// Read off the document, as the Admin Console does, so a language
				// switch changes the next request's Accept-Language.
				locale: () => document.documentElement.lang || null,
				// EP10-S02: the store every portal request is about.
				storeId: () => activeStore.current,
			}),
		[accessToken, caller],
	);
	return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}
